import { WorkflowState } from '@compass/client/types/case_record';
import {
  computeSlaValues,
  formatElapsedTime,
  formatMinutesAsHHMM,
  formatSLADuration,
  isSlaRiskBackendOwned,
  SLA_BREACH_SOON_LABEL,
  SLA_BREACHED_LABEL,
} from '@utils/sla';
import { determineSlaStatus, TERMINAL_VERBS } from '@utils/slaStatus';
import {
  AlertCircle,
  Ban,
  Check,
  CheckCircle2,
  HelpCircle,
  Hourglass,
  Pause,
  Send,
  Timer,
} from 'lucide-react';
import React from 'react';

import { KnipperCaseState, PqcDecision } from '@/constants/caseStates';
import { cn } from '@/utils';

import { Badge } from './badge';
import { CopyButton } from './copy-button';
import { SimpleTooltipV2 } from './tooltip';

interface SLATimerProps {
  workflowLastStartedAt?: string;
  workflowDurationSeconds?: number;
  slaMinutes?: number;
  isBreached?: boolean;
  completedAt?: string;
  workflowState?: WorkflowState;
  workflowType?: string;
  caseState?: string;
  /** When the case state was last updated. Used to compute elapsed-to-state for
   * intermediate states (submitted, acknowledged) and terminal-closed states. */
  stateUpdatedAt?: string;
  /** Manufacturer reply decision from output_data.data.decision (PQC). */
  decision?: string;
  /** Manufacturer ACK ID from output_data.data.ack_id (PQC). */
  ackId?: string;
  /** Backend SLA-risk escalation flag (status_flags.sla_risk_escalated). */
  slaRiskEscalated?: boolean;
  /** Workflow version (e.g. 'v3'). When 'v3' or 'v4', the breach-soon badge is driven
   * solely by `slaRiskEscalated` instead of the elapsed-percentage heuristic. */
  workflowVersion?: string;
  /** Display variant: 'list' for table cells, 'details' for the case detail page
   * header (preserves the pre-AD-2207 visual). */
  variant?: 'list' | 'details';
}

/**
 * Paused / running SLA indicator: a pause glyph while the workflow is paused
 * (the SLA clock is stopped), an hourglass while it is running. Shared by the
 * Cases list "Time remaining" column (via LegacySLATimer) and the Workforce
 * Console case card (BV-2247) so both surfaces show the same signal.
 */
export const SlaRunStateIcon: React.FC<{ paused: boolean; className?: string }> = ({
  paused,
  className,
}) =>
  paused ? (
    <Pause className={cn('w-3 h-3', className)} aria-label="SLA paused" />
  ) : (
    <Hourglass className={cn('w-3 h-3', className)} aria-label="SLA running" />
  );

/**
 * Legacy renderer preserved for non-PQC workflows and the case detail page header.
 * Faithfully reproduces the pre-AD-2207 visual so non-PQC list views and the detail
 * page are unaffected. The new vocabulary in the default renderer below is PQC-only.
 */
const LegacySLATimer: React.FC<SLATimerProps> = ({
  workflowLastStartedAt,
  workflowDurationSeconds,
  slaMinutes,
  isBreached,
  completedAt,
  workflowState,
  slaRiskEscalated,
  workflowVersion,
  variant,
}) => {
  if (!workflowLastStartedAt) return null;

  // The timer text and breach flag come from the shared resolver so this
  // component and the CSV/Excel export (BV-1846) can never disagree.
  const { slaTimer, slaFlag } = computeSlaValues({
    workflowLastStartedAt,
    workflowDurationSeconds,
    slaMinutes,
    isBreached,
    completedAt,
    workflowState,
    slaRiskEscalated,
    workflowVersion,
  });

  const isCompleted = !!completedAt;
  const isWorkflowPaused = workflowState === WorkflowState.WORKFLOW_STATE_PAUSED;

  // The details surface annotates the remaining time with the SLA budget.
  const displayTime =
    !isCompleted && variant === 'details' && slaMinutes !== undefined
      ? `${slaTimer} left/ ${formatSLADuration(slaMinutes)}`
      : slaTimer;

  let iconElement: React.ReactNode;
  if (isCompleted) {
    iconElement = <Check className="w-3 h-3" />;
  } else {
    iconElement = <SlaRunStateIcon paused={isWorkflowPaused} />;
  }

  return (
    <div className="inline-flex items-center gap-2">
      <div className="inline-flex items-center gap-1 whitespace-nowrap">
        {iconElement}
        <span className="text-sm">{displayTime}</span>
      </div>
      {slaFlag === SLA_BREACHED_LABEL && (
        <Badge variant="destructive" className="whitespace-nowrap">
          {SLA_BREACHED_LABEL}
        </Badge>
      )}
      {slaFlag === SLA_BREACH_SOON_LABEL && (
        <Badge variant="warning" className="whitespace-nowrap">
          {SLA_BREACH_SOON_LABEL}
        </Badge>
      )}
    </div>
  );
};

/**
 * Case states for which the details-surface SLA timer is never shown.
 * The status is conveyed elsewhere on the page (status badge, state-specific
 * sections) so the timer would just duplicate it. Applies regardless of
 * vocab — both LegacySLATimer (non-PQC workflows) and the new renderer
 * defer to this list when `variant === 'details'`.
 */
const DETAILS_HIDDEN_CASE_STATES = new Set<string>([
  KnipperCaseState.SUBMITTED,
  KnipperCaseState.ACKNOWLEDGED,
  KnipperCaseState.NON_REPORTABLE,
  KnipperCaseState.CANCELLED,
  KnipperCaseState.DUPLICATE,
  KnipperCaseState.FAILED,
  KnipperCaseState.TERMINATED,
]);

export const SLATimer: React.FC<SLATimerProps> = props => {
  // New vocabulary is PQC-only. Non-PQC workflows still use the legacy renderer.
  // Both list and details surfaces now use the new vocab for PQC; the details
  // surface trims the state set down to running + paused (see render() below)
  // since the case detail page only wants the timer + breach signals.
  const useNewVocab = props.workflowType === 'pqc';

  // AQ-165: the details surface is normally long-lived (the user can keep a case
  // open for hours). `determineSlaStatus` reads `moment()` during render, so we
  // need to force a re-render at a useful cadence to keep the timer alive. The
  // display format is HH:MM so a 60s tick is the right granularity. Hook is
  // unconditional (rules-of-hooks) — interval only attaches when we're on the
  // details surface.
  const [, forceTick] = React.useState(0);
  React.useEffect(() => {
    if (props.variant !== 'details') return undefined;
    const id = setInterval(() => forceTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [props.variant]);

  // Details-surface hide check applied before vocab branching so both the
  // legacy and new renderers honour it. Driven by case state directly rather
  // than the derived `status.kind`, which had subtle ordering bugs when
  // `workflow_state` was paused on a terminal case.
  if (
    props.variant === 'details' &&
    props.caseState &&
    DETAILS_HIDDEN_CASE_STATES.has(props.caseState)
  ) {
    return null;
  }

  if (!useNewVocab) {
    return <LegacySLATimer {...props} />;
  }

  let status = determineSlaStatus(props);
  const { caseState, variant } = props;

  // For v3/v4 workflows the running-phase breach-soon signal is owned by the
  // backend flag (status_flags.sla_risk_escalated), not the elapsed-percentage
  // heuristic baked into determineSlaStatus. Override only the running kinds;
  // submitted/acked/completed/terminal kinds are left untouched.
  if (
    isSlaRiskBackendOwned(props.workflowVersion) &&
    (status.kind === 'running_ok' || status.kind === 'running_near_breach')
  ) {
    status = { ...status, kind: props.slaRiskEscalated ? 'running_near_breach' : 'running_ok' };
  }

  if (status.kind === 'not_started') return null;

  // Defence-in-depth: even within the new-vocab path, only running + paused
  // kinds render on the details surface.
  const DETAILS_VISIBLE_KINDS = new Set([
    'running_ok',
    'running_near_breach',
    'running_breached',
    'paused',
  ]);
  if (variant === 'details' && !DETAILS_VISIBLE_KINDS.has(status.kind)) {
    return null;
  }

  const render = (): {
    icon: React.ReactNode;
    primary: React.ReactNode;
    badges?: React.ReactNode;
    tooltip: string;
  } => {
    switch (status.kind) {
      case 'running_ok': {
        const mins = status.primaryMinutes ?? 0;
        return {
          icon: <Hourglass className="w-3 h-3 text-muted-foreground" />,
          primary: <span className="text-sm tabular-nums">{formatMinutesAsHHMM(mins)}</span>,
          tooltip: `${formatElapsedTime(Math.max(0, mins))} to breach`,
        };
      }
      case 'running_near_breach': {
        const mins = status.primaryMinutes ?? 0;
        return {
          icon: <Hourglass className="w-3 h-3 text-attention-foreground" />,
          primary: (
            <span className="text-sm tabular-nums text-attention-foreground">
              {formatMinutesAsHHMM(mins)}
            </span>
          ),
          badges: (
            <Badge variant="warning" className="whitespace-nowrap">
              SLA Breach Soon
            </Badge>
          ),
          tooltip: `${formatElapsedTime(Math.max(0, mins))} to breach`,
        };
      }
      case 'running_breached': {
        const mins = status.primaryMinutes ?? 0;
        const overdueMins = Math.abs(mins);
        // Show only the "SLA Breached" badge; the negative timer (-01h 52m) crowds
        // the column. The exact overdue duration is still available via the tooltip.
        return {
          icon: <Timer className="w-3 h-3 text-destructive" />,
          primary: null,
          // The shared "SLA Breached" badge (driven by status.breached) renders in cellContent.
          tooltip: `Breached ${formatElapsedTime(overdueMins)} ago`,
        };
      }
      case 'paused': {
        const mins = status.primaryMinutes ?? 0;
        return {
          icon: <Pause className="w-3 h-3 text-muted-foreground" />,
          primary: <span className="text-sm tabular-nums">{formatMinutesAsHHMM(mins)}</span>,
          tooltip: `Paused · ${formatElapsedTime(Math.max(0, mins))} left`,
        };
      }
      case 'submitted_pending':
      case 'submitted_question':
      case 'submitted_missing_info': {
        const mins = status.primaryMinutes ?? 0;
        const decisionKey =
          status.kind === 'submitted_question'
            ? PqcDecision.QUESTION_ASKED
            : status.kind === 'submitted_missing_info'
              ? PqcDecision.MISSING_INFO
              : undefined;
        const decisionBadge = decisionKey ? (
          <Badge variant="progress" className="whitespace-nowrap inline-flex items-center gap-1">
            {decisionKey === PqcDecision.QUESTION_ASKED ? (
              <HelpCircle className="h-3 w-3" />
            ) : (
              <AlertCircle className="h-3 w-3" />
            )}
            {decisionKey === PqcDecision.QUESTION_ASKED ? 'Question asked' : 'Missing info'}
          </Badge>
        ) : null;
        // For question/missing-info, the badge itself conveys enough; hide the timer
        // text and rely on the tooltip for the submitted-in duration. For plain
        // `submitted_pending` we keep the timer since there's no badge to anchor on.
        const hidePrimaryTimer =
          status.kind === 'submitted_question' || status.kind === 'submitted_missing_info';
        return {
          icon: <Send className="w-3 h-3 text-progress-foreground" />,
          primary: hidePrimaryTimer ? null : (
            <span className="text-sm tabular-nums">{formatMinutesAsHHMM(mins)}</span>
          ),
          badges: decisionBadge,
          tooltip: `Submitted in ${formatElapsedTime(Math.max(0, mins))}`,
        };
      }
      case 'acked': {
        const mins = status.primaryMinutes ?? 0;
        const ackId = props.ackId;
        return {
          icon: <CheckCircle2 className="w-3 h-3 text-positive-foreground" />,
          primary: ackId ? (
            <span className="inline-flex items-center gap-1 group/ack">
              <span
                className="text-sm font-mono text-positive-foreground truncate max-w-[140px]"
                title={ackId}
              >
                {ackId}
              </span>
              <CopyButton
                value={ackId}
                label="ACK ID"
                className="opacity-0 group-hover/ack:opacity-100"
              />
            </span>
          ) : (
            <span className="text-sm text-positive-foreground">Ack received</span>
          ),
          tooltip: `Acked in ${formatElapsedTime(Math.max(0, mins))}`,
        };
      }
      case 'completed_met': {
        const mins = status.primaryMinutes ?? 0;
        return {
          icon: <CheckCircle2 className="w-3 h-3 text-positive-foreground" />,
          primary: <span className="text-sm tabular-nums">{formatMinutesAsHHMM(mins)}</span>,
          tooltip: `Completed in ${formatElapsedTime(Math.max(0, mins))}`,
        };
      }
      case 'completed_breached': {
        const mins = status.primaryMinutes ?? 0;
        return {
          icon: <CheckCircle2 className="w-3 h-3 text-destructive" />,
          primary: (
            <span className="text-sm tabular-nums text-destructive">
              {formatMinutesAsHHMM(mins)}
            </span>
          ),
          // The shared "SLA Breached" badge (driven by status.breached) renders in cellContent.
          tooltip: `Completed in ${formatElapsedTime(Math.max(0, mins))}`,
        };
      }
      case 'terminal_closed': {
        const mins = status.primaryMinutes ?? 0;
        const verb = (caseState && TERMINAL_VERBS[caseState]) || 'Closed';
        return {
          icon: <Ban className="w-3 h-3 text-muted-foreground" />,
          primary: (
            <span className="text-sm tabular-nums text-muted-foreground">
              {formatMinutesAsHHMM(mins)}
            </span>
          ),
          tooltip: `${verb} in ${formatElapsedTime(Math.max(0, mins))}`,
        };
      }
      default:
        return { icon: null, primary: null, tooltip: '' };
    }
  };

  const { icon, primary, badges, tooltip } = render();

  const cellContent = (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap')}>
      {icon}
      {primary}
      {status.breached && (
        <Badge variant="destructive" className="whitespace-nowrap">
          SLA Breached
        </Badge>
      )}
      {badges}
    </span>
  );

  if (!tooltip) return cellContent;
  return (
    <SimpleTooltipV2 content={tooltip} side="top">
      {cellContent}
    </SimpleTooltipV2>
  );
};
